import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Minus, Plus, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProductImage } from "@/components/RelicCard";
import { useProduct, useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";

const eur = (v) => `€${v.toLocaleString("en-US")}`;

export default function Product() {
  const { id } = useParams();
  const { product, loading, error } = useProduct(id);
  const { products } = useProducts();
  const { addItem, setOpen } = useCart();
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  if (loading)
    return <div className="pt-40 container-lux label-mono text-smoke">Summoning…</div>;
  if (error || !product)
    return (
      <div className="pt-40 container-lux text-center">
        <p className="heading-serif text-3xl mb-6">Fragrance not found</p>
        <Link to="/collection" className="label-mono text-blood">
          Return to the Collection
        </Link>
      </div>
    );

  const gallery = product.gallery?.length ? product.gallery : (product.image ? [product.image] : []);
  const inStock = (product.stock ?? 0) > 0;
  const preorder = !inStock;
  const hasNotes =
    (product.top_notes?.length || 0) + (product.heart_notes?.length || 0) + (product.base_notes?.length || 0) > 0;
  const related = products.filter((p) => p.slug !== product.slug).slice(0, 3);

  const add = (openDrawer) => {
    for (let i = 0; i < qty; i++) addItem(product);
    if (openDrawer) setOpen(true);
  };

  return (
    <div className="pt-28 md:pt-32 pb-24" data-testid={`product-page-${product.slug}`}>
      <div className="container-lux">
        <Link
          to="/collection"
          className="label-mono inline-flex items-center gap-2 mb-10 hover:text-blood transition-colors"
          data-testid="back-to-collection"
        >
          <ChevronLeft size={14} /> The Collection
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* GALLERY */}
          <div className="lg:sticky lg:top-28 self-start">
            <motion.div
              key={active}
              initial={{ opacity: 0.4, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden aspect-[4/5] bg-ink grain group"
            >
              <ProductImage
                product={gallery.length ? { ...product, image: gallery[active] } : product}
                className="w-full h-full relative z-[2]"
              />
              {preorder && (
                <span
                  className="absolute top-6 left-6 z-[3] label-mono bg-ink/70 backdrop-blur-sm text-blood border border-blood/50 px-3 py-1.5"
                  data-testid="product-preorder-badge"
                >
                  Pre-Order
                </span>
              )}
            </motion.div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-testid={`thumb-${i}`}
                    className={`w-20 h-24 overflow-hidden border ${active === i ? "border-blood" : "border-smoke/30"}`}
                  >
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {gallery.length > 1 && (
              <p className="label-mono text-smoke mt-3">Hover image to zoom · tap thumbnails for angles</p>
            )}
          </div>

          {/* DETAILS */}
          <div>
            <Reveal>
              <p className="label-mono mb-4 text-blood">{product.edition}</p>
              <h1 className="heading-serif text-5xl md:text-6xl leading-[0.95] mb-5">{product.name}</h1>
              <p className="text-base md:text-lg text-ivory/70 font-sans italic mb-6">{product.tagline}</p>
              <p className="font-mono text-sm text-smoke mb-8">
                {product.volume} · {product.concentration}
              </p>
              <p className="text-base md:text-lg text-ivory/80 font-sans leading-relaxed mb-10 max-w-lg">
                {product.story}
              </p>
            </Reveal>

            <div className="flex items-center justify-between border-y border-smoke/15 py-6 mb-8">
              <span className="heading-serif text-3xl">{eur(product.price)}</span>
              <span className="label-mono text-blood text-right" data-testid="stock-status">
                {inStock ? `${product.stock} in stock` : "Out of Stock — Available for Pre-Order"}
              </span>
            </div>

            {/* quantity + add */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex items-center border border-smoke/40">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-4 py-4 hover:text-blood"
                  data-testid="qty-decrease"
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span className="px-5 font-mono text-sm" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty((q) => Math.min(10, q + 1))}
                  className="px-4 py-4 hover:text-blood"
                  data-testid="qty-increase"
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={() => add(true)}
                data-testid="product-add-to-cart"
                className={`btn-relic flex-1 ${
                  preorder
                    ? "border-blood bg-blood text-ivory hover:bg-ivory hover:border-ivory hover:text-ink"
                    : "border-ivory bg-ivory text-ink hover:bg-blood hover:border-blood hover:text-ivory"
                }`}
              >
                {preorder ? "Pre-Order" : "Add to Bag"}
              </button>
            </div>

            {preorder && (
              <p className="label-mono text-smoke mb-10" data-testid="preorder-note">
                This fragrance is currently sold out. Place a pre-order to reserve yours.
              </p>
            )}

            {/* assurances */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 text-ivory/70">
              <Assurance icon={<Truck size={15} />} text="Shipped from the Netherlands within 3 working days" />
              <Assurance icon={<RotateCcw size={15} />} text="14-day returns on unopened fragrances" />
              <Assurance icon={<ShieldCheck size={15} />} text="Secure checkout via Stripe" />
            </div>

            {/* olfactory notes */}
            <div className="mb-12" data-testid="olfactory-pyramid">
              <p className="label-mono mb-6">The Composition</p>
              {hasNotes ? (
                [
                  { k: "Top", notes: product.top_notes },
                  { k: "Heart", notes: product.heart_notes },
                  { k: "Base", notes: product.base_notes },
                ].map((row) => (
                  <div key={row.k} className="border-t border-smoke/20 py-5">
                    <span className="label-mono text-smoke block mb-3">{row.k} Notes</span>
                    <div className="flex flex-wrap gap-2">
                      {(row.notes || []).map((n) => (
                        <span key={n} className="font-display text-lg md:text-xl px-4 py-1.5 border border-smoke/25">
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="border-t border-smoke/20 py-6">
                  <p className="text-ivory/60 font-sans leading-relaxed">
                    The olfactory composition for {product.name} will be revealed soon. Verified notes,
                    longevity and sillage are added by the house once confirmed.
                  </p>
                </div>
              )}
            </div>

            {/* specs */}
            <div className="space-y-4">
              <p className="label-mono mb-2">Specifications</p>
              <Spec label="Volume" value={product.volume} />
              <Spec label="Concentration" value={product.concentration} />
              {product.bottle_material ? <Spec label="Vessel" value={product.bottle_material} /> : null}
              <Spec label="Edition" value={product.edition} />
            </div>
          </div>
        </div>

        {/* RELATED */}
        {related.length > 0 && (
          <div className="mt-28" data-testid="related-fragrances">
            <p className="label-mono mb-10 text-blood">You may also be disturbed by</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} className="group" data-testid={`related-${p.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-ink mb-4">
                    <ProductImage product={p} className="w-full h-full" />
                  </div>
                  <div className="flex justify-between items-baseline">
                    <h3 className="heading-serif text-xl group-hover:text-blood transition-colors">{p.name}</h3>
                    <span className="font-mono text-sm">{eur(p.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Assurance({ icon, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-blood mt-0.5">{icon}</span>
      <span className="text-xs font-sans leading-snug">{text}</span>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-smoke/15 pb-4">
      <span className="label-mono text-smoke">{label}</span>
      <span className="text-sm text-ivory/85 text-right max-w-[62%] font-sans">{value}</span>
    </div>
  );
}
